"use client";

import { create } from "zustand";
import { BrandingEntityType } from "database";
import {
  DEFAULT_BRANDING_FONT,
  DEFAULT_BRANDING_LOGO,
  DEFAULT_BRANDING_PRIMARY_COLOR,
} from "@/lib/branding/constants";
import {
  brandingLevelFromEntityType,
  resolveBrandingCacheKey,
} from "@/lib/branding/cache-key";
import type {
  BrandingCacheKey,
  BrandingLevel,
  BrandingOverrides,
  EnsureBrandingParams,
  ResolvedBranding,
} from "@/lib/branding/types";

type BrandingStatus = "idle" | "loading" | "ready" | "error";

type BrandingState = {
  cache: Record<string, ResolvedBranding>;
  currentKey: BrandingCacheKey | null;
  current: ResolvedBranding | null;
  level: BrandingLevel;
  status: BrandingStatus;
  pageAccentColor: string | null;
  overrides: BrandingOverrides;
  hydrate: (
    entityType: BrandingEntityType | "DEFAULT",
    entityId: string,
    branding: ResolvedBranding,
    level?: BrandingLevel
  ) => void;
  setCurrentKey: (key: BrandingCacheKey | null, level: BrandingLevel) => void;
  setPageAccentColor: (color: string | null) => void;
  setOverrides: (overrides: BrandingOverrides) => void;
  clearEventOverrides: () => void;
  ensureBranding: (
    params: EnsureBrandingParams
  ) => Promise<ResolvedBranding | null>;
};

const DEFAULT_BRANDING: ResolvedBranding = {
  font: DEFAULT_BRANDING_FONT,
  primaryColor: DEFAULT_BRANDING_PRIMARY_COLOR,
  secondaryColor: DEFAULT_BRANDING_PRIMARY_COLOR,
  logo: DEFAULT_BRANDING_LOGO,
  favIcon: null,
};

/** Module-level in-flight map — prevents duplicate concurrent fetches. */
const inFlight = new Map<string, Promise<ResolvedBranding | null>>();

/** Higher = more specific. Prevents parent WL hydrate from overwriting EVENT. */
const LEVEL_PRIORITY: Record<BrandingLevel, number> = {
  DEFAULT: 0,
  WHITE_LABEL: 1,
  ORGANIZATION: 2,
  EVENT: 3,
};

function applyDefaults(branding: Partial<ResolvedBranding>): ResolvedBranding {
  return {
    font: branding.font ?? DEFAULT_BRANDING_FONT,
    primaryColor: branding.primaryColor ?? DEFAULT_BRANDING_PRIMARY_COLOR,
    secondaryColor:
      branding.secondaryColor ??
      branding.primaryColor ??
      DEFAULT_BRANDING_PRIMARY_COLOR,
    logo: branding.logo || DEFAULT_BRANDING_LOGO,
    favIcon: branding.favIcon ?? null,
    appVersionMinIosNetworkingApp:
      branding.appVersionMinIosNetworkingApp ?? null,
    appVersionMinAndroidNetworkingApp:
      branding.appVersionMinAndroidNetworkingApp ?? null,
  };
}

function brandingEquals(
  a: ResolvedBranding | null | undefined,
  b: ResolvedBranding | null | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.font === b.font &&
    a.primaryColor === b.primaryColor &&
    a.secondaryColor === b.secondaryColor &&
    a.logo === b.logo &&
    a.favIcon === b.favIcon &&
    a.appVersionMinIosNetworkingApp === b.appVersionMinIosNetworkingApp &&
    a.appVersionMinAndroidNetworkingApp === b.appVersionMinAndroidNetworkingApp
  );
}

function overridesEqual(a: BrandingOverrides, b: BrandingOverrides): boolean {
  return (
    a.logo === b.logo &&
    a.favIcon === b.favIcon &&
    a.primaryColor === b.primaryColor
  );
}

async function fetchBrandingFromApi({
  entityId,
  entityType,
  domain,
}: EnsureBrandingParams): Promise<ResolvedBranding | null> {
  const params = new URLSearchParams({
    id: entityId,
    entityType,
  });
  if (domain) params.set("domain", domain);

  const res = await fetch(`/api/branding?${params.toString()}`);
  if (!res.ok) return null;
  const data = await res.json();
  if (data?.error) return null;
  return applyDefaults(data);
}

export const useBrandingStore = create<BrandingState>((set, get) => ({
  cache: {},
  currentKey: null,
  current: null,
  level: "DEFAULT",
  status: "idle",
  pageAccentColor: null,
  overrides: {},

  hydrate: (entityType, entityId, branding, level) => {
    const key = resolveBrandingCacheKey(entityType, entityId);
    const resolved = applyDefaults(branding);
    const nextLevel = level ?? brandingLevelFromEntityType(entityType);
    const state = get();

    const cachedSame = brandingEquals(state.cache[key], resolved);
    const nextCache = cachedSame
      ? state.cache
      : { ...state.cache, [key]: resolved };

    // Always cache; only promote current when equal/higher specificity.
    // Stops parent WHITE_LABEL BrandingHydrator from clobbering EVENT after
    // the nested event layout hydrates (child layout effects run first).
    const canBecomeCurrent =
      !state.currentKey ||
      state.currentKey === key ||
      LEVEL_PRIORITY[nextLevel] >= LEVEL_PRIORITY[state.level];

    if (!canBecomeCurrent) {
      if (!cachedSame) set({ cache: nextCache });
      return;
    }

    if (
      cachedSame &&
      state.currentKey === key &&
      brandingEquals(state.current, resolved) &&
      state.level === nextLevel &&
      state.status === "ready"
    ) {
      return;
    }

    set({
      cache: nextCache,
      currentKey: key,
      current: resolved,
      level: nextLevel,
      status: "ready",
    });
  },

  setCurrentKey: (key, level) => {
    const state = get();
    const cached = key ? state.cache[key] : null;
    const nextCurrent =
      cached ?? (level === "DEFAULT" ? DEFAULT_BRANDING : null);
    const nextStatus =
      cached || level === "DEFAULT" ? ("ready" as const) : state.status;

    if (
      state.currentKey === key &&
      state.level === level &&
      state.current === nextCurrent &&
      state.status === nextStatus
    ) {
      return;
    }

    set({
      currentKey: key,
      current: nextCurrent,
      level,
      status: nextStatus,
    });
  },

  setPageAccentColor: (color) => {
    if (get().pageAccentColor === color) return;
    set({ pageAccentColor: color });
  },

  setOverrides: (overrides) => {
    const state = get();
    const next = { ...state.overrides, ...overrides };
    if (overridesEqual(state.overrides, next)) return;
    set({ overrides: next });
  },

  clearEventOverrides: () => {
    const state = get();
    if (
      state.pageAccentColor === null &&
      Object.keys(state.overrides).length === 0
    ) {
      return;
    }
    set({
      pageAccentColor: null,
      overrides: {},
    });
  },

  ensureBranding: async (params) => {
    const { entityId, entityType } = params;
    if (!entityId) return null;

    const key = resolveBrandingCacheKey(entityType, entityId);
    const cached = get().cache[key];
    if (cached) {
      const state = get();
      const nextLevel = brandingLevelFromEntityType(entityType);
      if (
        state.currentKey !== key ||
        state.current !== cached ||
        state.level !== nextLevel ||
        state.status !== "ready"
      ) {
        set({
          currentKey: key,
          current: cached,
          level: nextLevel,
          status: "ready",
        });
      }
      return cached;
    }

    const existing = inFlight.get(key);
    if (existing) {
      const state = get();
      if (state.status !== "loading" || state.currentKey !== key) {
        set({ status: "loading", currentKey: key });
      }
      const result = await existing;
      if (result) {
        const after = get();
        const nextLevel = brandingLevelFromEntityType(entityType);
        if (
          after.currentKey !== key ||
          after.current !== result ||
          after.level !== nextLevel ||
          after.status !== "ready"
        ) {
          set({
            currentKey: key,
            current: result,
            level: nextLevel,
            status: "ready",
          });
        }
      }
      return result;
    }

    set({ status: "loading", currentKey: key });

    const promise = fetchBrandingFromApi(params)
      .then((result) => {
        if (result) {
          set((state) => ({
            cache: { ...state.cache, [key]: result },
            currentKey: key,
            current: result,
            level: brandingLevelFromEntityType(entityType),
            status: "ready",
          }));
        } else if (get().status !== "error") {
          set({ status: "error" });
        }
        return result;
      })
      .catch(() => {
        if (get().status !== "error") {
          set({ status: "error" });
        }
        return null;
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, promise);
    return promise;
  },
}));

/**
 * Prefer primitive selectors below. This object selector is unsafe with
 * default Zustand equality (new object every call) — only use with useShallow.
 */
export function selectEffectiveBranding(state: BrandingState): ResolvedBranding {
  const base = state.current ?? DEFAULT_BRANDING;
  const { overrides } = state;
  return {
    ...base,
    primaryColor: overrides.primaryColor || base.primaryColor,
    logo: overrides.logo || base.logo,
    favIcon: overrides.favIcon ?? base.favIcon,
  };
}

export function selectEffectivePrimaryColor(state: BrandingState): string {
  return (
    state.pageAccentColor ||
    state.overrides.primaryColor ||
    state.current?.primaryColor ||
    DEFAULT_BRANDING_PRIMARY_COLOR
  );
}

export function selectEffectiveLogo(state: BrandingState): string {
  return state.overrides.logo || state.current?.logo || DEFAULT_BRANDING_LOGO;
}

export function selectEffectiveFavIcon(
  state: BrandingState
): string | null | undefined {
  return state.overrides.favIcon ?? state.current?.favIcon;
}

export function selectEffectiveFont(state: BrandingState): string {
  return state.current?.font ?? DEFAULT_BRANDING_FONT;
}

export { DEFAULT_BRANDING };
